import { useState, useMemo } from 'react';
import moment from 'moment';

export interface CalendarControl {
  // The currently focused month (represented as the 1st of the month)
  currentMonthMoment: moment.Moment;
  
  // Date range covering the full weeks of the month (Monday to Sunday)
  startDateMoment: moment.Moment;
  endDateMoment: moment.Moment;
  startDateStr: string;
  endDateStr: string;

  // Navigation functions
  goToPrevMonth: () => void;
  goToNextMonth: () => void;
  goToToday: () => void;
  setSpecificDate: (dateStr: string) => void;
}

export const useCalendarControl = (initialDateStr?: string): CalendarControl => {
  // Base date state. If not provided or invalid, defaults to today.
  const [baseDate, setBaseDate] = useState<moment.Moment>(() => {
    const init = moment(initialDateStr, 'YYYY-MM-DD', true);
    return init.isValid() ? init : moment();
  });

  // Calculate the derived dates whenever the baseDate changes
  const { 
    currentMonthMoment, 
    startDateMoment, 
    endDateMoment, 
    startDateStr, 
    endDateStr 
  } = useMemo(() => {
    // 1st of the current base date's month
    const monthStart = baseDate.clone().startOf('month');
    // End of the current base date's month
    const monthEnd = baseDate.clone().endOf('month');

    // Monday of the week that includes the start of the month
    // Note: isoWeek starts on Monday, isoWeekday(1) is Monday
    const startRange = monthStart.clone().startOf('isoWeek');
    
    // Sunday of the week that includes the end of the month
    // isoWeekday(7) is Sunday
    const endRange = monthEnd.clone().endOf('isoWeek');

    return {
      currentMonthMoment: monthStart,
      startDateMoment: startRange,
      endDateMoment: endRange,
      startDateStr: startRange.format('YYYY-MM-DD'),
      endDateStr: endRange.format('YYYY-MM-DD'),
    };
  }, [baseDate]);

  // Navigation functions
  const goToPrevMonth = () => {
    setBaseDate((prev) => prev.clone().subtract(1, 'month'));
  };

  const goToNextMonth = () => {
    setBaseDate((prev) => prev.clone().add(1, 'month'));
  };

  const goToToday = () => {
    setBaseDate(moment());
  };

  const setSpecificDate = (dateStr: string) => {
    const newDate = moment(dateStr, 'YYYY-MM-DD', true);
    if (newDate.isValid()) {
      setBaseDate(newDate);
    }
  };

  return {
    currentMonthMoment,
    startDateMoment,
    endDateMoment,
    startDateStr,
    endDateStr,
    goToPrevMonth,
    goToNextMonth,
    goToToday,
    setSpecificDate,
  };
};

export type CalendarControlData = ReturnType<typeof useCalendarControl>;
